/**
 * Server errors, translated by `code` rather than by the English text.
 *
 * The API answers `{ error, code }`. The `error` string is written for
 * developers and is always English, so it is never rendered: the `code` is the
 * stable contract and this file is the only place it becomes something a trader
 * reads. A code we do not know yet falls back to a message chosen from the HTTP
 * status, so an unmapped code degrades to vague-but-Russian rather than to
 * English leaking into the interface.
 *
 * Codes are collected from `AppError(...)` in apps/api-server/src.
 */

const BY_CODE: Record<string, string> = {
  // ---------- Authentication ----------
  invalid_credentials: 'Неверная почта или пароль.',
  invalid_token: 'Сессия недействительна или истекла. Войдите заново.',
  missing_token: 'Сессия не найдена. Войдите заново.',
  session_revoked: 'Эта сессия была завершена. Войдите заново.',
  session_not_found: 'Сессия не найдена — возможно, она уже завершена.',
  unauthenticated: 'Нужно войти в аккаунт.',
  wrong_token_type: 'Сессия недействительна. Войдите заново.',
  invalid_ticket: 'Подключение к живой ленте истекло. Обновите страницу.',
  account_deactivated: 'Аккаунт отключён. Свяжитесь с поддержкой.',
  trader_unavailable: 'Аккаунт трейдера недоступен. Свяжитесь с поддержкой.',
  trader_not_found: 'Аккаунт трейдера не найден.',
  email_taken: 'На эту почту уже зарегистрирован аккаунт.',

  // ---------- Two-factor ----------
  totp_required: 'Введите код из приложения-аутентификатора.',
  invalid_totp: 'Код не подошёл. Проверьте, что время на телефоне точное, и попробуйте следующий код.',
  twofa_required: 'Для этого аккаунта обязательна двухфакторная аутентификация.',
  twofa_already_enabled: 'Двухфакторная аутентификация уже включена.',
  twofa_not_started: 'Сначала запустите настройку двухфакторной аутентификации.',
  twofa_misconfigured:
    'Двухфакторная аутентификация настроена некорректно. Свяжитесь с поддержкой.',
  invalid_password: 'Неверный пароль.',

  // ---------- Access ----------
  forbidden: 'Недостаточно прав для этого действия.',
  ip_not_allowed: 'Вход с этого IP-адреса запрещён.',
  csrf_failed: 'Запрос отклонён по соображениям безопасности. Обновите страницу и попробуйте снова.',
  rate_limited: 'Слишком много запросов. Подождите немного и попробуйте снова.',

  // ---------- Transactions ----------
  transaction_not_found: 'Транзакция не найдена. Обновите список.',
  payment_not_found: 'Платёж не найден. Обновите список.',
  missing_payment_id: 'Не указан платёж.',
  invalid_status:
    'Статус платежа изменился — действие больше недоступно. Обновите список и посмотрите текущее состояние.',
  concurrent_update: 'Платёж изменился, пока вы работали с ним. Обновите список и попробуйте снова.',
  deadline_passed: 'Срок подтверждения истёк. Платёж передан администратору.',
  confirm_failed: 'Платёж не удалось подтвердить. Обновите список и посмотрите его статус.',
  reference_conflict: 'Конфликт по номеру платежа.',
  bad_reference: 'Некорректный номер платежа.',

  // ---------- Disputes ----------
  dispute_exists: 'По этой транзакции уже открыт спор. Дождитесь решения администратора.',
  dispute_not_found: 'Спор не найден.',
  already_resolved: 'Спор уже решён.',

  // ---------- Verification and cards ----------
  kyc_not_found: 'Данные верификации не найдены.',
  kyc_already_approved: 'Верификация уже пройдена.',
  kyc_no_documents: 'Загрузите документы, прежде чем отправлять на проверку.',
  card_not_found: 'Карта не найдена.',
  card_not_verified: 'Основной можно сделать только проверенную карту.',
  card_last4_mismatch: 'Последние четыре цифры не совпадают с номером карты.',
  trader_kyc_not_approved: 'Верификация ещё не пройдена.',
  trader_no_verified_card: 'Нет ни одной проверенной карты.',

  // ---------- Uploads ----------
  missing_file: 'Прикрепите файл.',
  empty_file: 'Файл пустой.',
  file_too_large: 'Файл больше допустимого размера.',
  too_many_files: 'Прикреплено слишком много файлов.',
  unexpected_file_field: 'Файл отправлен не в то поле.',
  upload_rejected: 'Загрузка отклонена.',
  invalid_file_reference: 'Некорректная ссылка на файл.',
  file_not_found: 'Файл не найден.',

  // ---------- Money: withdrawals, collateral, limits ----------
  insufficient_funds:
    'На доступном балансе не хватает средств. Выводить можно только заработанную комиссию.',
  insufficient_balance:
    'На доступном балансе не хватает средств. Выводить можно только заработанную комиссию.',
  insufficient_collateral:
    'Не хватает свободного залога. Пополните депозит, чтобы принимать платежи.',
  invalid_address: 'Адрес не похож на TRC-20: он начинается с T и состоит из 34 символов.',
  invalid_trc20_address:
    'Адрес не похож на TRC-20: он начинается с T и состоит из 34 символов.',
  amount_too_small: 'Сумма меньше минимальной для вывода.',
  amount_too_large: 'Сумма больше доступной к выводу.',
  invalid_amount: 'Некорректная сумма.',
  withdrawal_exists: 'Предыдущая заявка на вывод ещё рассматривается. Дождитесь решения по ней.',
  withdrawal_not_found: 'Заявка на вывод не найдена.',
  withdrawal_not_pending: 'Статус заявки уже изменился. Обновите страницу.',
  traffic_blocked: 'Приём платежей сейчас невозможен — причина указана на «Обзоре».',
  kyc_not_approved: 'Верификация ещё не пройдена.',
  no_usable_card: 'Нет карты, готовой принять платёж. Проверьте «Реквизиты».',
  card_disabled: 'Карта выключена. Включите её, чтобы принимать платежи.',
  card_limit_invalid: 'Лимит указан некорректно. Введите сумму в рублях или 0 — без ограничения.',
  invalid_limits: 'Лимит указан некорректно. Введите сумму в рублях или 0 — без ограничения.',
  limit_exceeded: 'Значение выходит за допустимые пределы.',

  // ---------- Generic ----------
  validation_error: 'Проверьте заполненные поля — часть данных некорректна.',
  not_found: 'Не найдено.',
  entry_not_found: 'Запись не найдена.',
  conflict: 'Данные изменились. Обновите страницу и попробуйте снова.',
  internal_error: 'Ошибка на сервере. Попробуйте ещё раз через минуту.',
};

/** When the code is unknown, the status still says roughly what went wrong. */
const BY_STATUS: Array<{ matches: (status: number) => boolean; message: string }> = [
  { matches: (s) => s === 401, message: 'Нужно войти заново.' },
  { matches: (s) => s === 403, message: 'Недостаточно прав для этого действия.' },
  { matches: (s) => s === 404, message: 'Не найдено. Обновите страницу.' },
  {
    matches: (s) => s === 409 || s === 422,
    message: 'Данные изменились. Обновите страницу и попробуйте снова.',
  },
  { matches: (s) => s === 413, message: 'Файл слишком большой.' },
  {
    matches: (s) => s === 429,
    message: 'Слишком много запросов. Подождите немного и попробуйте снова.',
  },
  { matches: (s) => s >= 400 && s < 500, message: 'Запрос отклонён. Проверьте данные.' },
  { matches: (s) => s >= 500, message: 'Ошибка на сервере. Попробуйте ещё раз через минуту.' },
];

/**
 * @param code   `code` from the error envelope, if the server sent one.
 * @param status HTTP status, used only when the code is unknown.
 */
export function messageForCode(
  code: string | undefined,
  status: number | undefined,
): string | undefined {
  if (code) {
    const known = BY_CODE[code];
    if (known) return known;
  }
  if (status !== undefined) {
    return BY_STATUS.find((entry) => entry.matches(status))?.message;
  }
  return undefined;
}
